import { IProduct, IProductCategory, IStore, IUser, NormalizedIProduct } from '@snackpass/snackpass-types';
import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import { compact, compose, flatten, map, prop, propOr } from 'lodash/fp';
import { RootState } from '../store';
import { LoadingStatus } from '../types';
import api, { GetMenuResponse } from '../../API';
import _ from 'lodash';

interface StoresState {
    stores: IStore[] | null;
    activeStore: IStore | null;
    status: LoadingStatus;
    categories: IProductCategory[];
    products: NormalizedIProduct[];
}

const initialState = {
    stores: null,
    activeStore: null,
    status: LoadingStatus.Idle,
    categories: [],
    products: [],
} as StoresState;

const _sanitizeStoreResponse = compose(compact, flatten, map(prop('store')));

export const fetchStores = createAsyncThunk(
    'stores/fetchStores',
    async (user: IUser): Promise<IStore[] | null> => {
        try {
            const storeIds: string[] = propOr(
                [],
                'snackpassPermissions.storeIds',
                user
            );
            // TODO: change this to an endpoint that fetches all of a user's
            // stores instead of fetching each individually using n api calls
            const res = await Promise.all(
                storeIds.map(
                    async (storeId: string) => await api.stores.getOne(storeId)
                )
            );

            return _sanitizeStoreResponse(res.map(prop('data')));
        } catch (err) {
            return null;
        }
    }
);

export const fetchStoreMenu = createAsyncThunk(
    'stores/fetchMenu',
    async (storeId: string): Promise<GetMenuResponse | null> => {
        try {
            const resp = await api.stores.getMenu(storeId);
            return resp.data;
        } catch (err) {
            console.log(err);
            return null;
        }
    }
);

const storesSlice = createSlice({
    name: 'stores',
    initialState,
    reducers: {
        createProduct(
            state,
            action: PayloadAction<{ storeId: string; product: IProduct }>
        ) {
            const { payload } = action;

            if (
                state.activeStore &&
                payload.storeId === state.activeStore._id &&
                state.activeStore.products
            ) {
                state.activeStore.products.push(payload.product);
            }
        },
        updateProduct(
            state,
            action: PayloadAction<{ storeId: string; product: IProduct }>
        ) {
            const { payload } = action;
            if (state.products) {
                const updatedProduct = state.products.findIndex(
                    p => p._id === payload.product._id
                );
                if (updatedProduct !== -1) {
                    state.products[updatedProduct].name = payload.product.name;
                    state.products[updatedProduct].price = payload.product.price;
                    state.products[updatedProduct].priceByWeight = payload.product.priceByWeight;
                    state.products[updatedProduct].soldOut = payload.product.soldOut;
                }
            }
        },
        deleteProduct(
            state,
            action: PayloadAction<{ storeId: string; productId: string }>
        ) {
            const { payload } = action;
            if (
                state.activeStore &&
                payload.storeId === state.activeStore._id &&
                state.activeStore.products
            ) {
                const deletedProduct = state.activeStore.products.findIndex(
                    p => p._id === payload.productId
                );
                if (deletedProduct && deletedProduct !== -1) {
                    state.activeStore.products.splice(deletedProduct, 1);
                }
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchStores.pending, (state: StoresState) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(
                fetchStores.fulfilled,
                (
                    state: StoresState,
                    action: PayloadAction<IStore[] | null>
                ) => {
                    const stores = action.payload;
                    state.stores = stores;
                    state.activeStore = stores && stores[0];
                    state.status = LoadingStatus.Success;
                }
            )
            .addCase(fetchStores.rejected, (state: StoresState) => {
                state.status = LoadingStatus.Failed;
            })
            .addCase(fetchStoreMenu.pending, (state: StoresState) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(fetchStoreMenu.fulfilled, (state, action) => {
                if (state.activeStore) {
                    state.status = LoadingStatus.Success;
                    state.categories = action.payload?.categories ?? [];
                    state.products = action.payload?.products ?? [];
                }
            })
            .addCase(fetchStoreMenu.rejected, state => {
                state.status = LoadingStatus.Failed;
            });
    },
});

export const getStoresState = (state: RootState) => state.stores;
export const getStores = createSelector([getStoresState], prop('stores'));
export const getActiveStore = createSelector(
    [getStoresState],
    state => state.activeStore
);
export const getLoadingStatus = createSelector(
    [getStoresState],
    state => state.status
);
export const getProducts = createSelector(
    [getStoresState],
    state => state.products,
)
export const getProductMap = createSelector(
    [getProducts],
    (products) => _.keyBy(products, "_id")
)
export const getCategories = createSelector(
    [getStoresState],
    (state) => state.categories,
);

export const { createProduct, updateProduct, deleteProduct } =
    storesSlice.actions;
export const storesReducer = storesSlice.reducer;
